import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Share2, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ShareModal = ({ analysisId, open, onOpenChange }) => {
  const [enabled, setEnabled] = useState(false);
  const [expiration, setExpiration] = useState('never');
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Validate analysisId - must be a valid UUID, not 'new' or undefined
  const isValidId = analysisId && analysisId !== 'new' && analysisId !== 'undefined';

  useEffect(() => {
    if (open && isValidId) {
      fetchShareStatus();
    }
  }, [open, analysisId, isValidId]);

  const fetchShareStatus = async () => {
    if (!isValidId) {
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/technical-analyses/${analysisId}`);
      const analysis = response.data;
      
      setEnabled(analysis.is_public_share_enabled || false);
      
      if (analysis.is_public_share_enabled && analysis.public_share_token) {
        const baseUrl = window.location.origin;
        setShareUrl(`${baseUrl}/compartilhar/analise-tecnica/${analysis.public_share_token}`);
      }
      
      // Determine expiration
      if (analysis.public_share_expires_at) {
        const expiresDate = new Date(analysis.public_share_expires_at);
        const now = new Date();
        const diffDays = Math.ceil((expiresDate - now) / (1000 * 60 * 60 * 24));
        
        if (diffDays <= 7) {
          setExpiration('7days');
        } else if (diffDays <= 30) {
          setExpiration('30days');
        } else {
          setExpiration('never');
        }
      } else {
        setExpiration('never');
      }
    } catch (error) {
      console.error('Error fetching share status:', error);
    }
  };

  const handleToggleShare = async (newEnabled) => {
    setEnabled(newEnabled);
    await updateShare(newEnabled, expiration);
  };

  const handleExpirationChange = async (newExpiration) => {
    setExpiration(newExpiration);
    if (enabled) {
      await updateShare(enabled, newExpiration);
    }
  };

  const updateShare = async (isEnabled, exp) => {
    setLoading(true);
    try {
      let expiresAt = null;
      
      if (isEnabled && exp !== 'never') {
        const now = new Date();
        if (exp === '7days') {
          now.setDate(now.getDate() + 7);
        } else if (exp === '30days') {
          now.setDate(now.getDate() + 30);
        }
        expiresAt = now.toISOString();
      }
      
      if (!isValidId) {
        toast.error('Salve a análise antes de compartilhar');
        setLoading(false);
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/api/technical-analyses/${analysisId}/share`,
        {
          enabled: isEnabled,
          expiresAt
        }
      );
      
      // Build full URL using window.location.origin + relative path from backend
      if (response.data.public_share_path) {
        const fullShareUrl = window.location.origin + response.data.public_share_path;
        setShareUrl(fullShareUrl);
      } else {
        setShareUrl('');
      }
      
      toast.success(isEnabled ? 'Compartilhamento ativado!' : 'Compartilhamento desativado');
    } catch (error) {
      toast.error('Erro ao atualizar compartilhamento');
      console.error('Error updating share:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiado para a área de transferência.');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erro ao copiar link');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Compartilhar análise técnica</DialogTitle>
          <DialogDescription>
            Gere um link público para compartilhar esta análise
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Toggle Switch */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-slate-900 font-medium">Compartilhamento por link</Label>
              <p className="text-sm text-slate-600">
                {enabled ? 'Ativo' : 'Inativo'}
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleToggleShare}
              disabled={loading}
            />
          </div>

          {enabled ? (
            <>
              {/* Expiration Selector */}
              <div className="space-y-2">
                <Label className="text-slate-900 font-medium">Expiração do link</Label>
                <Select value={expiration} onValueChange={handleExpirationChange} disabled={loading}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Nunca expira</SelectItem>
                    <SelectItem value="7days">Expira em 7 dias</SelectItem>
                    <SelectItem value="30days">Expira em 30 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Share URL */}
              {shareUrl && (
                <div className="space-y-2">
                  <Label className="text-slate-900 font-medium">Link de compartilhamento</Label>
                  <div className="flex gap-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="flex-1 text-slate-600"
                    />
                    <Button
                      onClick={copyToClipboard}
                      size="sm"
                      variant="outline"
                      className={copied ? "bg-green-500 text-white border-green-500 hover:bg-green-600" : ""}
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-600">
                    Qualquer pessoa com este link poderá visualizar esta análise
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6">
              <Share2 className="h-12 w-12 mx-auto mb-2 opacity-50 text-slate-500" />
              <p className="text-sm text-slate-600">
                Esta análise não está sendo compartilhada no momento.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
